import React, { useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { Button, Paper, Typography } from '@material-ui/core';
import amber from '@material-ui/core/colors/amber';
import grey from '@material-ui/core/colors/grey';
import { makeStyles } from '@material-ui/core/styles';

import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import LinkOffIcon from '@material-ui/icons/LinkOff';

// O menu em que o usuario estava define para onde o botao "inicio" leva.
const MENU_PARA_ROTA = {
  rel: '/app/rel',
  cad: '/app/cad',
  fin: '/app/fin',
  conf: '/app/config',
  backof: '/app/backof',
  com: '/app/com',
  cob: '/app/cob',
};

const ROTA_PADRAO = '/app/cob';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Altura util = viewport - header - footer - padding vertical do .app-wrapper.
    // 70px e 50px sao $app-bar-height e $footer-height (styles/global/_variables.scss);
    // o padding de 30px vem de styles/layout/_main-content.scss.
    minHeight: 'calc(100vh - 70px - 50px - 60px)',
    [theme.breakpoints.down('xs')]: {
      // abaixo de 576px o .app-wrapper usa padding de 20px
      minHeight: 'calc(100vh - 70px - 50px - 40px)',
    },
  },
  cartao: {
    width: '100%',
    maxWidth: 480,
    padding: theme.spacing(6, 5, 4),
    borderRadius: 12,
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,.04), 0 12px 32px rgba(0,0,0,.08)',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(4, 3, 3),
    },
  },
  circuloIcone: {
    width: 88,
    height: 88,
    margin: '0 auto',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: amber[50],
  },
  icone: {
    fontSize: 44,
    color: amber[800],
  },
  titulo: {
    marginTop: theme.spacing(3),
    fontWeight: 500,
    color: grey[900],
  },
  descricao: {
    marginTop: theme.spacing(1.5),
    color: grey[600],
    lineHeight: 1.6,
  },
  acoes: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    // margem nos filhos em vez de 'gap': flex-gap nao cobre todo o browserslist.
    '& > *': {
      margin: theme.spacing(0.75),
    },
  },
  // O amber do tema usa contrastText branco, que fica ilegivel (contraste ~1.8:1).
  // Texto escuro sobre o mesmo amber sobe para ~11:1 sem sair da identidade.
  // O '&&' dobra a especificidade: sem isso a regra do MuiButton-containedPrimary
  // vence e o texto volta a ficar branco (invisivel sobre o amarelo).
  botaoPrimario: {
    '&&': {
      backgroundColor: amber[500],
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: 'none',
    },
    '&&:hover': {
      backgroundColor: amber[600],
      boxShadow: 'none',
    },
  },
  rodape: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${grey[200]}`,
  },
  endereco: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Courier New", monospace',
    fontSize: 12,
    color: grey[600],
    wordBreak: 'break-all',
  },
  legenda: {
    color: grey[500],
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    fontSize: 11,
  },
}));

const Error404 = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const rotaInicio = useMemo(() => {
    try {
      return MENU_PARA_ROTA[localStorage.getItem('user_menu')] || ROTA_PADRAO;
    } catch (error) {
      return ROTA_PADRAO;
    }
  }, []);

  const podeVoltar = history.length > 1;

  return (
    <div className={`app-wrapper ${classes.container}`}>
      <Paper className={classes.cartao} elevation={0}>
        <div className={classes.circuloIcone}>
          <LinkOffIcon className={classes.icone} aria-hidden="true" />
        </div>

        <Typography variant="h5" component="h1" className={classes.titulo}>
          Não encontramos esta página
        </Typography>

        <Typography variant="body1" className={classes.descricao}>
          O endereço pode ter mudado ou o link que você usou está desatualizado.
          Use o menu lateral para chegar à tela que procura.
        </Typography>

        <div className={classes.acoes}>
          <Button
            variant="contained"
            className={classes.botaoPrimario}
            startIcon={<HomeOutlinedIcon />}
            component={Link}
            to={rotaInicio}
          >
            Ir para o início
          </Button>

          {podeVoltar && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.goBack()}
            >
              Voltar
            </Button>
          )}
        </div>

        <div className={classes.rodape}>
          <Typography variant="caption" className={classes.legenda}>
            Erro 404 · endereço não encontrado
          </Typography>
          <code className={classes.endereco}>{location.pathname}</code>
        </div>
      </Paper>
    </div>
  );
};

export default Error404;
